// src/pages/apikey/ApikeyListPage.tsx
// API key console — tenant-wide list, create (one-time secret display), rotate, suspend/activate,
// revoke/delete (typed confirmation), usage & audit drill-down. Replaces the generated CRUD scaffold.
// See docs/ux/apikey-ia.md for the full RPC placement.

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useApikey, useI18n } from '@react/hooks';
import { apikeyApi } from '@core/api';
import {
  Button, Card,
  Alert, ErrorAlert,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
} from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { StatusBadge } from '@/components/StatusBadge';
import { PermissionMatrix } from './PermissionMatrix';
import { TypedConfirmDialog } from '@/features/apikey/TypedConfirmDialog';
import type { APIKey } from '@core/types/apikey';
import {
  statusVariant, statusLabelKey, canRevoke, canDelete, canSuspend, canActivate, canRegenerate,
  permissionsFromKey, permissionCount, permissionMatrixEntries,
  EMPTY_KEY_FORM, validateKeyForm,
  formatDate, formatDateTime, formatExpiration, formatKeyPrefix, formatLatency, formatErrorRate,
  revokeConfirmPhrase,
} from '@/features/apikey/logic';
import type { KeyFormState } from '@/features/apikey/logic';

// --- Inline SVG icons ---

function PlusIcon({ className }: { className?: string }) {
  return (<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>);
}
function CopyIcon({ className }: { className?: string }) {
  return (<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>);
}
function CheckIcon({ className }: { className?: string }) {
  return (<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>);
}
function ChevronIcon({ className, open }: { className?: string; open: boolean }) {
  return (<svg className={`${className ?? ''} transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>);
}

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const input = document.createElement('textarea');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);
  return { copied, copy };
}

// --- One-time secret banner (create + regenerate share this) ---

function OneTimeSecretBanner({ fullKey, onDismiss }: { fullKey: string; onDismiss: () => void }) {
  const { t } = useI18n();
  const { copied, copy } = useCopyToClipboard();
  return (
    <Alert variant="success" dismissible onDismiss={onDismiss}>
      <div className="space-y-3">
        <p className="text-sm font-medium">{t('apikey.createdKey.title')}</p>
        <p className="text-xs text-muted-foreground">{t('apikey.createdKey.warning')}</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2 rounded-md bg-muted text-foreground font-mono text-sm break-all select-all">
            {fullKey}
          </code>
          <Button variant="secondary" size="sm" onClick={() => copy(fullKey)} aria-label={t('common.copy')}>
            {copied ? <CheckIcon className="h-4 w-4 text-green-600" /> : <CopyIcon className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </Alert>
  );
}

// --- Create dialog ---
// FormDialog per docs/ux/14-forms.md: name (TitleInput) → description (AutoGrowTextarea) →
// PermissionMatrix (width="lg" — the matrix is a big/complex control) → never-expires toggle
// + conditional expiry date. Direct apikeyApi.CreateAPIKey call preserved (not the store) —
// CreateAPIKeyResponse.key is the one-time secret, which the store's wrapper discards.

function CreateKeyDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (fullKey: string) => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState<KeyFormState>({ ...EMPTY_KEY_FORM });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const errors = validateKeyForm(form);
  const canSubmit = Object.keys(errors).length === 0;
  const dirty = !!(form.name.trim() || form.description.trim() || Object.keys(form.permissions).length > 0);

  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const reset = () => {
    setForm({ ...EMPTY_KEY_FORM });
    setTouched({});
    setSubmitError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!canSubmit || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const req: Record<string, unknown> = {
        name: form.name.trim(),
        description: form.description.trim(),
        scopes: [],
        permission_matrix: permissionMatrixEntries(form.permissions),
        never_expires: form.neverExpires,
      };
      if (!form.neverExpires && form.expiresAt) {
        req.expires_at = new Date(form.expiresAt).toISOString();
      }
      // Direct API call (not the store) — CreateAPIKeyResponse.key is the one-time secret,
      // which the store's CreateAPIKey wrapper discards.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await apikeyApi.CreateAPIKey(req as any);
      const fullKey = response.key;
      reset();
      onCreated(fullKey);
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('apikey.createKey.submit')}
      canSubmit={canSubmit}
      isSubmitting={isSubmitting}
      dirty={dirty}
      width="lg"
      aria-label={t('apikey.createKey.title')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
        <div onBlur={() => markTouched('name')}>
          <TitleInput
            value={form.name}
            onChange={(v) => setForm((prev) => ({ ...prev, name: v }))}
            placeholder={t('apikey.field.namePlaceholder')}
            initialFocus
            onEnter={() => document.getElementById('apikey-create-description')?.focus()}
            aria-label={t('apikey.field.name')}
          />
          {touched.name && errors.name && <p className="mt-1 text-xs text-destructive">{t(errors.name)}</p>}
        </div>
        <div>
          <AutoGrowTextarea
            id="apikey-create-description"
            value={form.description}
            onChange={(v) => setForm((prev) => ({ ...prev, description: v }))}
            onBlur={() => markTouched('description')}
            placeholder={t('apikey.field.descriptionPlaceholder')}
            minRows={2}
          />
          {touched.description && errors.description && (
            <p className="mt-1 text-xs text-destructive">{t(errors.description)}</p>
          )}
        </div>

        <div className="space-y-2 pt-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('apikey.field.permissions')}
          </p>
          <p className="text-xs text-muted-foreground">{t('apikey.field.permissionsHelp')}</p>
          <PermissionMatrix
            value={form.permissions}
            onChange={(permissions) => {
              setForm((prev) => ({ ...prev, permissions }));
              markTouched('permissions');
            }}
          />
          {touched.permissions && errors.permissions && (
            <p className="text-xs text-destructive">{t(errors.permissions)}</p>
          )}
        </div>

        <div className="pt-1">
          <ToggleSwitch
            label={t('apikey.field.neverExpires')}
            checked={form.neverExpires}
            onChange={(checked) =>
              setForm((prev) => ({ ...prev, neverExpires: checked, expiresAt: checked ? '' : prev.expiresAt }))
            }
          />
        </div>
        {!form.neverExpires && (
          <FormRow label={t('apikey.field.expiresAt')} labelWidth="sm">
            <input
              type="date"
              value={form.expiresAt}
              onChange={(e) => setForm((prev) => ({ ...prev, expiresAt: e.target.value }))}
              onBlur={() => markTouched('expiresAt')}
              className="rounded-md border border-input bg-transparent px-2 py-1 text-sm text-foreground"
            />
            {touched.expiresAt && errors.expiresAt && (
              <p className="mt-1 text-xs text-destructive">{t(errors.expiresAt)}</p>
            )}
          </FormRow>
        )}
      </div>
    </FormDialog>
  );
}

// --- Row detail (usage + audit, lazy-loaded on expand) ---

function KeyDetail({ apiKey }: { apiKey: APIKey }) {
  const { t } = useI18n();
  const store = useApikey();
  const keyId = apiKey.entity?.id ?? '';
  const [tab, setTab] = useState<'permissions' | 'usage' | 'audit'>('permissions');
  const [usage, setUsage] = useState<Awaited<ReturnType<typeof store.GetAPIKeyUsage>>['usage']>();
  const [history, setHistory] = useState<Awaited<ReturnType<typeof store.GetAPIKeyUsageHistory>>['history']>();
  const [logs, setLogs] = useState<Awaited<ReturnType<typeof store.GetAPIKeyAuditLogs>>['logs']>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!keyId) return;
    setLoading(true);
    if (tab === 'usage') {
      Promise.all([store.GetAPIKeyUsage(keyId), store.GetAPIKeyUsageHistory(keyId)])
        .then(([u, h]) => { setUsage(u.usage); setHistory(h.history); })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else if (tab === 'audit') {
      store.GetAPIKeyAuditLogs(keyId)
        .then((r) => setLogs(r.logs))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [tab, keyId]);

  const currentPermissions = permissionsFromKey(apiKey);
  const pCount = permissionCount(currentPermissions);

  return (
    <div className="space-y-3">
      <div role="tablist" className="flex gap-1 border-b border-border">
        {(['permissions', 'usage', 'audit'] as const).map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={tab === v}
            onClick={() => setTab(v)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {t(`apikey.detail.tab.${v}`)}
            {v === 'permissions' && pCount > 0 && ` (${pCount})`}
          </button>
        ))}
      </div>

      {tab === 'permissions' && (
        <PermissionMatrix value={currentPermissions} onChange={() => {}} disabled />
      )}

      {tab === 'usage' && (
        loading ? (
          <p className="text-sm text-muted-foreground py-4">{t('common.loading')}</p>
        ) : usage ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground">{t('apikey.usage.total')}</p>
              <p className="text-lg font-semibold text-foreground">{usage.total_requests}</p>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground">{t('apikey.usage.errorRate')}</p>
              <p className="text-lg font-semibold text-foreground">{formatErrorRate(usage.failed_requests, usage.total_requests)}</p>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground">{t('apikey.usage.rateLimited')}</p>
              <p className="text-lg font-semibold text-foreground">{usage.rate_limited_requests}</p>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-xs text-muted-foreground">{t('apikey.usage.avgLatency')}</p>
              <p className="text-lg font-semibold text-foreground">{formatLatency(usage.average_latency_ms)}</p>
            </div>
            {history?.items && history.items.length > 0 && (
              <div className="col-span-full text-xs text-muted-foreground">
                {t('apikey.usage.historyPoints', { count: history.items.length })}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4">{t('apikey.usage.empty')}</p>
        )
      )}

      {tab === 'audit' && (
        loading ? (
          <p className="text-sm text-muted-foreground py-4">{t('common.loading')}</p>
        ) : logs?.items && logs.items.length > 0 ? (
          <ul className="space-y-1.5">
            {logs.items.map((log, i) => (
              <li key={i} className="flex items-center justify-between text-sm border-b border-border/50 py-1.5">
                <span className="text-foreground">{log.action}</span>
                <span className="text-xs text-muted-foreground">{formatDateTime(log.occurred_at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground py-4">{t('apikey.audit.empty')}</p>
        )
      )}
    </div>
  );
}

// --- Row ---

function KeyRow({ apiKey, onAction }: {
  apiKey: APIKey;
  onAction: (action: 'edit' | 'rotate' | 'suspend' | 'activate' | 'revoke' | 'delete', key: APIKey) => void;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const keyId = apiKey.entity?.id ?? '';

  return (
    <>
      <TableRow>
        <TableCell>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="flex items-center gap-2 text-left w-full"
          >
            <ChevronIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" open={expanded} />
            <div>
              <p className="text-sm font-medium text-foreground">{apiKey.name}</p>
              {apiKey.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{apiKey.description}</p>}
            </div>
          </button>
        </TableCell>
        <TableCell>
          <code className="px-1.5 py-0.5 rounded bg-muted text-foreground font-mono text-xs">
            {formatKeyPrefix(apiKey.key_prefix)}
          </code>
        </TableCell>
        <TableCell><StatusBadge variant={statusVariant(apiKey.status)} label={t(statusLabelKey(apiKey.status))} /></TableCell>
        <TableCell className="text-sm text-muted-foreground">{formatDate(apiKey.created_at)}</TableCell>
        <TableCell className="text-sm text-muted-foreground">{apiKey.last_used_at ? formatDate(apiKey.last_used_at) : t('apikey.neverUsed')}</TableCell>
        <TableCell className="text-sm text-muted-foreground">{formatExpiration(apiKey)}</TableCell>
        <TableCell className="text-right">
          <div className="flex justify-end gap-1 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => onAction('edit', apiKey)}>{t('common.edit')}</Button>
            {canRegenerate(apiKey) && (
              <Button variant="ghost" size="sm" onClick={() => onAction('rotate', apiKey)}>{t('apikey.action.rotate')}</Button>
            )}
            {canSuspend(apiKey) && (
              <Button variant="ghost" size="sm" onClick={() => onAction('suspend', apiKey)}>{t('apikey.action.suspend')}</Button>
            )}
            {canActivate(apiKey) && (
              <Button variant="ghost" size="sm" onClick={() => onAction('activate', apiKey)}>{t('apikey.action.activate')}</Button>
            )}
            {canRevoke(apiKey) && (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onAction('revoke', apiKey)}>{t('apikey.action.revoke')}</Button>
            )}
            {canDelete(apiKey) && (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onAction('delete', apiKey)}>{t('common.delete')}</Button>
            )}
          </div>
        </TableCell>
      </TableRow>
      {expanded && keyId && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/20 px-6 py-4">
            <KeyDetail apiKey={apiKey} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// --- Edit dialog ---
// Treated as a FormDialog (not true per-field inline edit) — the permission matrix doesn't fit
// a property-grid row shape, so this is the "heavy composer" exception from docs/ux/14-forms.md,
// same width="lg" shell as create for consistency.

function EditKeyDialog({
  open,
  apiKey,
  onClose,
  onSaved,
}: {
  open: boolean;
  apiKey: APIKey | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const store = useApikey();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  // The dialog stays mounted between opens (parent gates via `open`), so re-seed the draft
  // from the target key each time it (re)opens.
  useEffect(() => {
    if (open && apiKey) {
      setName(apiKey.name);
      setDescription(apiKey.description);
      setPermissions(permissionsFromKey(apiKey));
      setSubmitError(null);
    }
  }, [open, apiKey]);

  const canSubmit = !!name.trim();
  const dirty =
    !!apiKey &&
    (name !== apiKey.name ||
      description !== apiKey.description ||
      JSON.stringify(permissions) !== JSON.stringify(permissionsFromKey(apiKey)));

  const close = () => onClose();

  const submit = async () => {
    if (!apiKey?.entity?.id || !canSubmit || isSaving) return;
    setIsSaving(true);
    setSubmitError(null);
    try {
      await store.UpdateAPIKey(apiKey.entity.id, {
        api_key: {
          name,
          description,
          permission_matrix: { entries: permissionMatrixEntries(permissions) },
        } as APIKey,
        field_mask: ['name', 'description', 'permission_matrix'],
      });
      onSaved();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('common.save')}
      canSubmit={canSubmit}
      isSubmitting={isSaving}
      dirty={dirty}
      width="lg"
      aria-label={t('apikey.editKey.title')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={t('apikey.field.namePlaceholder')}
          initialFocus
          onEnter={() => document.getElementById('apikey-edit-description')?.focus()}
          aria-label={t('apikey.field.name')}
        />
        <AutoGrowTextarea
          id="apikey-edit-description"
          value={description}
          onChange={setDescription}
          placeholder={t('apikey.field.descriptionPlaceholder')}
          minRows={2}
        />
        <div className="space-y-2 pt-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('apikey.field.permissions')}
          </p>
          <PermissionMatrix value={permissions} onChange={setPermissions} />
        </div>
      </div>
    </FormDialog>
  );
}

// --- Suspend dialog (reason + optional until) ---

function SuspendDialog({
  open,
  apiKey,
  onConfirm,
  onClose,
  isLoading,
}: {
  open: boolean;
  apiKey: APIKey | null;
  onConfirm: (reason: string, until?: string) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setUntil('');
    }
  }, [open]);

  const dirty = !!(reason.trim() || until);

  const submit = () => {
    onConfirm(reason.trim(), until || undefined);
  };

  if (!open) return null;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('apikey.action.suspend')}
      isSubmitting={isLoading}
      dirty={dirty}
      aria-label={t('apikey.suspend.title')}
      context={
        apiKey ? (
          <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">{apiKey.name}</span>
        ) : undefined
      }
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {apiKey && <p className="text-sm text-muted-foreground">{t('apikey.suspend.description', { name: apiKey.name })}</p>}
        <AutoGrowTextarea
          value={reason}
          onChange={setReason}
          placeholder={t('apikey.suspend.reasonPlaceholder')}
          aria-label={t('apikey.suspend.reason')}
          minRows={2}
        />
        <FormRow label={t('apikey.suspend.until')} hint={t('apikey.suspend.untilHelp')} labelWidth="sm">
          <input
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="rounded-md border border-input bg-transparent px-2 py-1 text-sm text-foreground"
          />
        </FormRow>
      </div>
    </FormDialog>
  );
}

// --- Main page ---

export const ApikeyListPage = observer(() => {
  const store = useApikey();
  const { t } = useI18n();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<APIKey | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<APIKey | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<APIKey | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<APIKey | null>(null);
  const [isActing, setIsActing] = useState(false);

  const reload = useCallback(() => {
    store.ListAPIKeys({
      pagination: { cursor: '', limit: 100, direction: 'forward' },
      sort: [],
      view: 0,
    });
  }, [store]);

  useEffect(() => { reload(); }, [reload]);

  const handleAction = (action: 'edit' | 'rotate' | 'suspend' | 'activate' | 'revoke' | 'delete', key: APIKey) => {
    const keyId = key.entity?.id;
    if (!keyId) return;
    switch (action) {
      case 'edit':
        // GetAPIKey — the authoritative single-key read, in case the list row is stale.
        store.GetAPIKey(keyId).then(setEditTarget).catch(() => setEditTarget(key));
        break;
      case 'suspend':
        setSuspendTarget(key);
        break;
      case 'revoke':
        setRevokeTarget(key);
        break;
      case 'delete':
        setDeleteTarget(key);
        break;
      case 'activate':
        store.ActivateAPIKey(keyId, {}).then(reload).catch(() => {});
        break;
      case 'rotate':
        store.RegenerateAPIKey(keyId, { keep_permissions: true, keep_restrictions: true })
          .then((resp) => { setCreatedKey(resp.key); reload(); })
          .catch(() => {});
        break;
    }
  };

  const doSuspend = async (reason: string, until?: string) => {
    if (!suspendTarget?.entity?.id) return;
    setIsActing(true);
    try {
      await store.SuspendAPIKey(suspendTarget.entity.id, {
        reason,
        suspend_until: until ? new Date(until).toISOString() : undefined,
      });
      setSuspendTarget(null);
      reload();
    } finally {
      setIsActing(false);
    }
  };

  const doRevoke = async () => {
    if (!revokeTarget?.entity?.id) return;
    setIsActing(true);
    try {
      await store.RevokeAPIKey(revokeTarget.entity.id, { reason: t('apikey.revoke.defaultReason') });
      setRevokeTarget(null);
      reload();
    } finally {
      setIsActing(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    setIsActing(true);
    try {
      await store.DeleteAPIKey(deleteTarget.entity.id);
      setDeleteTarget(null);
      reload();
    } finally {
      setIsActing(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('apikey.console.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('apikey.console.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/apikeys/policies" className="text-sm text-primary hover:underline">
            {t('apikey.console.policiesLink')}
          </Link>
          <Button onClick={() => { setShowCreateForm(true); setCreatedKey(null); }}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('apikey.createKey.button')}
          </Button>
        </div>
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {createdKey && <OneTimeSecretBanner fullKey={createdKey} onDismiss={() => setCreatedKey(null)} />}

      <CreateKeyDialog
        open={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onCreated={(fullKey) => { setCreatedKey(fullKey); setShowCreateForm(false); reload(); }}
      />

      <EditKeyDialog
        open={!!editTarget}
        apiKey={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { setEditTarget(null); reload(); }}
      />

      <SuspendDialog
        open={!!suspendTarget}
        apiKey={suspendTarget}
        onConfirm={doSuspend}
        onClose={() => setSuspendTarget(null)}
        isLoading={isActing}
      />

      <TypedConfirmDialog
        open={!!revokeTarget}
        title={t('apikey.revoke.title')}
        description={t('apikey.revoke.description')}
        phrase={revokeTarget ? revokeConfirmPhrase(revokeTarget) : ''}
        confirmLabel={t('apikey.action.revoke')}
        isConfirming={isActing}
        onConfirm={doRevoke}
        onCancel={() => setRevokeTarget(null)}
      />

      <TypedConfirmDialog
        open={!!deleteTarget}
        title={t('apikey.deleteKey.title')}
        description={t('apikey.deleteKey.description')}
        phrase={deleteTarget ? revokeConfirmPhrase(deleteTarget) : ''}
        confirmLabel={t('common.delete')}
        isConfirming={isActing}
        onConfirm={doDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('apikey.column.name')}</TableHead>
              <TableHead>{t('apikey.column.key')}</TableHead>
              <TableHead>{t('apikey.column.status')}</TableHead>
              <TableHead>{t('apikey.column.created')}</TableHead>
              <TableHead>{t('apikey.column.lastUsed')}</TableHead>
              <TableHead>{t('apikey.column.expires')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={7} message={t('apikey.console.empty')} />
            ) : (
              store.items.map((key) => (
                <KeyRow key={key.entity?.id} apiKey={key} onAction={handleAction} />
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default ApikeyListPage;
